import { applyCategoryLabelMaps } from './reportingCategoryLabelMaps';

/** TTL for chart execution result cache (ms). */
export const CHART_RESULT_CACHE_TTL_MS = 5 * 60 * 1000;

/** Max concurrent chart API executions. */
export const CHART_EXECUTE_CONCURRENCY = 5;

/** Debounce delay when global filters change (ms). */
export const CHART_FILTER_DEBOUNCE_MS = 250;

/** Wait after expanding a section before firing chart requests (lets collapse animation finish). */
export const CHART_EXPAND_DEBOUNCE_MS = 450;

/** Distinct values in a multi-dimension summary dataset (e.g. dv_cosd_summary). */
export function listDatasetDimensions(dataset) {
    if (!dataset?.columns?.some(c => c.key === 'dimension')) return [];
    const values = new Set();
    for (const row of dataset.rows ?? []) {
        const dim = row.dimension ?? row.Dimension;
        if (dim != null && String(dim).trim() !== '') values.add(String(dim));
    }
    return [...values].sort((a, b) => a.localeCompare(b));
}

export function applyDimensionFilter(dataset, dimensionFilter) {
    if (!dataset?.rows || !dimensionFilter) return dataset;

    const dimKey = dataset.columns?.find(c => c.key === 'dimension')?.key ?? 'dimension';
    const hasDimension = dataset.columns?.some(c => c.key === dimKey);
    if (!hasDimension) return dataset;

    const filteredRows = dataset.rows.filter(row => {
        const dim = row[dimKey] ?? row.dimension ?? row.Dimension;
        return String(dim) === dimensionFilter;
    });

    const columns = (dataset.columns ?? []).filter(c => c.key !== dimKey);
    const rows = filteredRows.map(row => {
        const next = { ...row };
        delete next[dimKey];
        delete next.dimension;
        delete next.Dimension;
        return next;
    });

    return { ...dataset, columns, rows };
}

/**
 * Apply client-side dimension filter and optional category label maps before chart/stat rendering.
 * @param {object} chartDef
 * @param {object|null} rawDataset
 * @param {Record<string, Record<string, string>>} [labelMapsLookup]
 */
export function processChartDataset(chartDef, rawDataset, labelMapsLookup) {
    if (!rawDataset) return null;
    const filtered = applyDimensionFilter(rawDataset, chartDef?.dimensionFilter);
    return applyCategoryLabelMaps(filtered, {
        dimensionKey: chartDef?.dimensionFilter,
        labelMapsLookup,
        encoding: chartDef?.viz?.encoding,
    });
}

/**
 * Re-apply label maps to cached API results (no refetch).
 * @param {Array<object>} chartItems
 * @param {string} filterSignature
 * @param {Record<string, Record<string, string>>} labelMapsLookup
 */
export function reprocessCachedChartResults(chartItems, filterSignature, labelMapsLookup) {
    const results = {};
    for (const chart of chartItems) {
        if (!chart?.functionId) continue;
        const cached = sharedChartResultCache.get(chartExecuteCacheKey(chart.functionId, filterSignature));
        if (!cached) continue;
        const rawDataset = pickDatasetFromResult(cached, chart.datasetKey ?? 'default');
        results[chart.id] = processChartDataset(chart, rawDataset, labelMapsLookup);
    }
    return results;
}

/**
 * @param {object} dataset
 * @param {string} [datasetKey]
 */
export function pickDatasetFromResult(result, datasetKey = 'default') {
    if (!result || result.error) return null;
    return result?.datasets?.[datasetKey] ?? result?.datasets?.default ?? null;
}

/**
 * Simple in-memory cache with TTL.
 */
export function createChartResultCache() {
    const map = new Map();

    return {
        get(key) {
            const entry = map.get(key);
            if (!entry) return null;
            if (Date.now() > entry.expiresAt) {
                map.delete(key);
                return null;
            }
            return entry.data;
        },
        set(key, data) {
            map.set(key, { data, expiresAt: Date.now() + CHART_RESULT_CACHE_TTL_MS });
        },
        clear() {
            map.clear();
        },
        delete(key) {
            map.delete(key);
        },
    };
}

/** Shared across dashboard panels — survives collapse/expand. */
export const sharedChartResultCache = createChartResultCache();

export function clearSharedChartResultCache() {
    sharedChartResultCache.clear();
}

/**
 * Run async tasks with a concurrency limit.
 * @param {Array<() => Promise<void>>} tasks
 * @param {number} [limit]
 */
export async function runWithConcurrencyPool(tasks, limit = CHART_EXECUTE_CONCURRENCY) {
    if (tasks.length === 0) return;

    let index = 0;
    const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
        while (index < tasks.length) {
            const i = index++;
            await tasks[i]();
        }
    });
    await Promise.all(workers);
}

/**
 * Cache key for a function execution (shared across charts using same function + filters).
 * @param {string} functionId
 * @param {string} filterSignature
 */
export function chartExecuteCacheKey(functionId, filterSignature) {
    return `${functionId}:${filterSignature}`;
}

/**
 * Drop cached API results for the given charts (same keys used on execute).
 * @param {Array<{ functionId?: string }>} charts
 * @param {string} filterSignature
 */
export function invalidateChartCacheForCharts(charts, filterSignature) {
    if (!charts?.length || !filterSignature) return;
    const seen = new Set();
    for (const chart of charts) {
        if (!chart?.functionId) continue;
        const key = chartExecuteCacheKey(chart.functionId, filterSignature);
        if (seen.has(key)) continue;
        seen.add(key);
        sharedChartResultCache.delete(key);
    }
}
